export const PWA_LIFECYCLE_EVENT = 'capychess:pwa-lifecycle';
export const PWA_ACTIVATION_TIMEOUT_MS = 8_000;

export type PwaLifecycleDetail =
  | Readonly<{ readonly phase: 'offline-ready' }>
  | Readonly<{
      readonly phase: 'update-ready';
      readonly activate: () => Promise<void>;
    }>
  | Readonly<{ readonly phase: 'reload-required' }>;

export type PwaActivationResult =
  | Readonly<{ readonly ok: true }>
  | Readonly<{ readonly ok: false; readonly reason: 'activation-failed' | 'timeout' }>;

/**
 * Posts the activation request only after the acknowledgment listener exists.
 * Resolving the message-post promise is not sufficient: the update is accepted
 * only when the browser reports that the new worker actually controls the page.
 */
export function activateAndWaitForReloadRequired(
  activate: () => Promise<void>,
  timeoutMs = PWA_ACTIVATION_TIMEOUT_MS,
): Promise<PwaActivationResult> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (result: PwaActivationResult) => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timeout);
      window.removeEventListener(PWA_LIFECYCLE_EVENT, handleLifecycle);
      resolve(result);
    };
    const handleLifecycle = (event: Event) => {
      const detail = (event as CustomEvent<PwaLifecycleDetail>).detail;
      if (detail.phase === 'reload-required') {
        finish({ ok: true });
      }
    };
    const timeout = window.setTimeout(() => {
      finish({ ok: false, reason: 'timeout' });
    }, timeoutMs);

    window.addEventListener(PWA_LIFECYCLE_EVENT, handleLifecycle);
    void Promise.resolve()
      .then(activate)
      .catch(() => finish({ ok: false, reason: 'activation-failed' }));
  });
}
